import { getHours, getMinutes, isDate, isValid } from "date-fns";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCIIconTypes, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Attachments from "src/components/Attachments";
import Tabs from "src/components/Tabs";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { convertIFileDTOArrayToFileArray } from "src/Interfaces/fileDTO";
import List from "src/components/List";
import formatDate from "src/utils/formatDate";
import Title from "src/components/Title";
import { ModalCenter } from "src/components/Modal/styles";
import { ITypeModal } from "src/components/Modal/interface";
import { FormMeetingAction, IMeetingFormDTO, IRefModalFormMeeting, IResourceDTO } from "../../interface";
import { FormSchema } from "../../schemas";
import TabActions from "./modalTabs/tabActions";
import TabGeneralData from "./modalTabs/tabGeneralData";
import ParticipantsTab from "./modalTabs/tabParticipants";

const ModalFormMeeting: React.ForwardRefRenderFunction<IRefModalFormMeeting> = (_,ref) => {

	const { translate,currentLanguage} = useTranslation();
	const {user} = useAuth();
	const { confirm } = useConfirmModal();
	const { addToast} = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const controllerUrl = "Meetings/";
	const initialValue = {
		ara001Assunto:"",
		ara001Dthrfim:new Date(),
		ara001Dthrinicio:new Date(),
		ara001Efa005Id:user.efa005Id.toString(),
		ara001Efa005Nome:user.efa005Nome,
		ara001Finalizada:"N",
		ara001Id:0,
		ara001Pauta:"",
		ara001Taa008Id:"",
		participants:[],
		tags:[]
	};
	const [meetingData, setMeetingData] = useState<IMeetingFormDTO>(initialValue);
	const [resourcesData, setResourcesData] = useState<IResourceDTO[]>();
	const [formAction,setFormAction] = useState<FormMeetingAction>(FormMeetingAction.ADD);

	const onCloseFunction = useRef<Function|undefined>();
	const confirmResourcesFunction = useRef<Function|undefined>();
	const modalRef = useRef<RCCRefModalProps>(null);
	const resourcesModalRef = useRef<RCCRefModalProps>(null);
	
	const {
		register,
		control,
		formState:{errors},
		setError,
		watch,
		clearErrors,
		setValue,
		reset,
		handleSubmit
	}= useForm<IMeetingFormDTO>();
	
	const parcipantsControl = useFieldArray({
		control,
		name: "participants"
	});

	const submitForm = async(data: any) => {
		const isAdd = formAction === FormMeetingAction.ADD || formAction === FormMeetingAction.COPY;
		try{
			const dataForm = {...meetingData,...data} as IMeetingFormDTO;
			clearErrors();
			modalRef.current?.setLoadingButton();
			dataForm.participants = data.participants?.map((p:any) => (
				{
					...p, 
					ara002Acordoata: p.ara002Acordoata && p.ara002Acordoata !== "N" ? "S" : "N", 
					ara002Presente:p.ara002Presente && p.ara002Presente !== "N" ? "S" : "N"
				})
			);

			if(formAction === FormMeetingAction.REOPEN || formAction === FormMeetingAction.COPY){
				dataForm.ara001Finalizada = "N";
				dataForm.participants = dataForm.participants?.map((p) => (
					{
						...p,
						ara002Acordoata:"N",
						ara002Comentario:"",
						ara002Presente:"N"
					})
				);
			}

			dataForm.ara001Diasfinalizar = dataForm.ara001Diasfinalizar || undefined;
			dataForm.ara001Mea001Id = dataForm.ara001Mea001Id || undefined;
			dataForm.ara001Taa010Id = dataForm.ara001Taa010Id || undefined;

			if((isDate(dataForm.ara001Dthrfim)&& isValid(dataForm.ara001Dthrfim)) && (isDate(dataForm.ara001Dthrinicio)&&isValid(dataForm.ara001Dthrinicio))){
				const endHour = getHours(dataForm.ara001Dthrfim);
				const endMinutes = getMinutes(dataForm.ara001Dthrfim);
				dataForm.ara001Dthrfim.setTime(dataForm.ara001Dthrinicio.getTime());
				dataForm.ara001Dthrfim.setHours(endHour,endMinutes);
			}

			const offset = dataForm.ara001Dthrfim.getTimezoneOffset();
			dataForm.ara001Dthrfim = new Date(dataForm.ara001Dthrfim.getTime() - (offset*60*1000));
			dataForm.ara001Dthrinicio = new Date(dataForm.ara001Dthrinicio.getTime() - (offset*60*1000));

			if(typeof data.tags === "string"){
				if(data.tags){
					dataForm.tags = data.tags.split(",");
				}
				else{
					dataForm.tags = [];
				}
			}else if(dataForm.tags && dataForm.tags.length > 0)
				dataForm.tags = dataForm.tags.map((tag: any) => {
					if(typeof tag === "object")
						return tag.value;
					return tag;
				});

			await FormSchema(translate).validate({...dataForm}, {
				abortEarly: false
			});

			if (dataForm.participants && await verifyresources(dataForm)){
				confirmResourcesFunction.current = () => {
					resourcesModalRef.current?.closeModal();
					verifyCloseMeeting(dataForm,isAdd);
				};
				resourcesModalRef.current?.openModal();
			}else{
				await verifyCloseMeeting(dataForm,isAdd);
			}
			
			modalRef.current?.resetLoadingButton();
		}catch(err){
			modalRef.current?.resetLoadingButton();
			if(getValidationErrors(err,setError)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar reunião") : translate("Erro ao alterar reunião"), addToast);
		}
	};

	const formRequest = async(data:IMeetingFormDTO,isAdd:boolean) => {
		try{
			
			let response;
			const formData = new FormData();
			formData.append("Obj", JSON.stringify({
				...data
			}));

			data.attachments?.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			if(isAdd){
				
				response = await RCCServicesApi.postRequest({
					method: `${controllerUrl}Insert`,
					contentType: "multipart/form-data",
					body: formData
				});
			}else{
				
				response = await RCCServicesApi.putRequest({
					method: `${controllerUrl}Update`,
					contentType: "multipart/form-data",
					body: formData
				});
			}
	
			addToast({
				color: "green",
				description: isAdd ? translate("Reunião adicionada com sucesso") : translate("Reunião alterada com sucesso"),
				title: translate("Sucesso")
			});

			if(onCloseFunction.current)
				onCloseFunction.current(response.data);

			modalRef.current?.resetLoadingButton();
			modalRef.current?.closeModal();

		}catch(err){
			modalRef.current?.resetLoadingButton();
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar reunião") : translate("Erro ao alterar reunião"), addToast);
		}
	};

	const verifyCloseMeeting = async(data:IMeetingFormDTO,isAdd:boolean) => {
		try{
			
			if(formAction === FormMeetingAction.EXECUTE){
				await confirm(
					translate("Finalizar a reunião"), 
					`${translate("Deseja finalizar a reunião")}?`,
					async () => {
						await formRequest({...data, ara001Finalizada : "S"},isAdd);
					},
					() => {
						modalRef.current?.resetLoadingButton();
					},
					false,
					async () => {
						await formRequest(data,isAdd);
					}
				);
			}else{
				await formRequest(data,isAdd);
			}

		}catch(err){
			modalRef.current?.resetLoadingButton();
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar reunião") : translate("Erro ao alterar reunião"), addToast);
		}
	};
	
	const verifyresources = async(data:IMeetingFormDTO) => {
		try {
			const response = await RCCServicesApi.putRequest({
				method: `${controllerUrl}VerifyResources`,
				body: {
					...data,
					participants: data.participants.filter((e) => e.ara002Efa005Id && e.ara002Efa005Id !== "")
				}
			});
			
			if(response.data !== null && response.data.length > 0){
				setResourcesData(response.data);
				return true;
			} 

			return false;
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao verificar recursos") , addToast);
			return false;
		}
	};

	const requestMeeting = async(id:number,mode:FormMeetingAction) =>
	{
		const {data} = await RCCServicesApi.getRequest({
			method: `${controllerUrl}Select`,
			urlParameters:{id,mode}
		});

		let dataFormatted: File[] = [];

		if(data.attachments && data.attachments?.length > 0)
			dataFormatted = convertIFileDTOArrayToFileArray(data.attachments);

		if(data.ara001Finalizada === "S" && (mode !== FormMeetingAction.COPY && mode !== FormMeetingAction.REOPEN && mode !== FormMeetingAction.VIEW) )
			setFormAction(FormMeetingAction.VIEW);

		setMeetingData({...data,attachments:dataFormatted});
		setValue("ara001Ata", data.ara001Ata);
		parcipantsControl.append(data.participants);

	};

	const titleHandler = {
		"1":{title:translate("Adicionar reunião"),icon:"plus"},
		"2":{title:translate("Alterar reunião"),icon:"pencil"},
		"3":{title:translate("Copiar reunião"),icon:"copy"},
		"4":{title:translate("Executar reunião"),icon:"play"},
		"5":{title:translate("Visualizar reunião"),icon:"eye"},
		"6":{title:translate("Reabrir reunião"),icon:"check"}
	};

	const actionButtonAdd = (onClose?:(row?:IMeetingFormDTO)=>void) => {
		reset();
		setFormAction(FormMeetingAction.ADD);
		setMeetingData(initialValue);
		onCloseFunction.current = onClose;
		modalRef.current?.openModal();
	};

	const actionButtonRequest = async(id:number,action:FormMeetingAction,onClose?:(row?:IMeetingFormDTO)=>void) => {
		try {
			setLoading(true);
			reset();
			setFormAction(action);
			await requestMeeting(id,action);
			onCloseFunction.current = onClose;
			setLoading(false);
			modalRef.current?.openModal();
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar dados"), addToast);
			setLoading(false);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonRequest
	}));

	return (
		<Modal 
			type={formAction === FormMeetingAction.COPY ? ITypeModal.COPY : undefined}
			header={{
				title: titleHandler[formAction].title,
				subtitle: formAction !== FormMeetingAction.ADD ? `${translate("Reunião")}: ${meetingData?.ara001Assunto}` : "",
				icon: {name:titleHandler[formAction].icon as RCCIIconTypes}
			}}
			footer={{
				showSaveButton: formAction !== FormMeetingAction.VIEW,
				buttonLoadingText: translate("Carregando"),
				saveButtonText: translate("Salvar"),
				cancelButtonText: translate("Cancelar")
			}}
			ref={modalRef}
			onSubmit={handleSubmit(submitForm)}
		>
			<Tabs 
				name="meetingsTab" 
				tabInit={0} 
				options={[
					{
						label:translate("Dados"),
						content: 
							<TabGeneralData
								data={meetingData}
								control={control}
								register={register}
								errors={errors}
								formAction={formAction}
								setValue={setValue}
								watch={watch}
							/>
					},
					{
						label:translate("Participantes"),
						content: 
							<ParticipantsTab
								participantControls={parcipantsControl}
								control={control}
								register={register}
								errors={errors}
								formAction={formAction}
								clearErrors={clearErrors}
								setValue={setValue}
								responsible={meetingData.ara001Efa005Id}
							/>
					},
					{
						label:translate("Anexos"),
						content: 
							<Attachments
								name="attachments"
								setValue={setValue as any}
								filesInit={meetingData.attachments}
								disabled={formAction === FormMeetingAction.VIEW}
							/>,
						hidden:formAction === FormMeetingAction.ADD || formAction === FormMeetingAction.CHANGE || formAction === FormMeetingAction.REOPEN
					},
					{
						label:translate("Ações"),
						content: 
							<TabActions
								actions={meetingData.actions||[]}
								meetingId={meetingData.ara001Id}
								formAction={formAction}
								responsible={meetingData.ara001Efa005Id}
							/>,
						hidden:formAction === FormMeetingAction.ADD || formAction === FormMeetingAction.CHANGE || formAction === FormMeetingAction.REOPEN || formAction === FormMeetingAction.COPY
					}
				]}
			/>
			<Modal 
				header={{
					title: translate("Recursos ocupados")
				}}
				ref={resourcesModalRef}
				withoutForm
				onSubmit={() => confirmResourcesFunction.current?.()}
				styleInline={`${ModalCenter} height:70vh;`}
				footer={{
					showSaveButton: true,
					saveButtonText: translate("Continuar"),
					cancelButtonText: translate("Cancelar")
				}}
			>
				<RCCGrid>
					{resourcesData?.some((x) => !!x.taa008Descricao && !x.efa005Nome) &&
					<>
						<Title color="defaultColor" marginBottom={5} marginTop={10}>
							{translate("A sala de reunião")}&nbsp;<b>{resourcesData[0].taa008Descricao}</b>&nbsp;{translate("está ocupada nos seguintes horários")}.
						</Title>
						<List 
							showHeader
							data={resourcesData?.filter((x) => !!x.taa008Descricao && !x.efa005Nome)}
							columns={[{
								idColumn: "ara001Assunto",
								nameColumn: translate("Assunto"),
								xs: 12,
								sm: 6,
								justify: "left",
								truncate: true
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 12,
								sm: 4,
								justify: "left",
								truncate: true,
								cellRenderer: (row:any) => <span>{formatDate(row.ara001Dthrinicio,currentLanguage,true)} - {formatDate(row.ara001Dthrfim,currentLanguage,true,"dd/MM/yyyy")}</span>
							}]}
						/>
						<br/>
					</>
					}
					{resourcesData?.some((x) => !!x.efa005Nome) &&
					<>
						<Title color="defaultColor" marginBottom={5} marginTop={10}>
							{translate("Os participantes abaixo estão ocupados nesta data")}.
						</Title>
						<List 
							showHeader
							data={resourcesData?.filter((x) => !!x.efa005Nome)}
							columns={[{
								idColumn: "efa005Nome",
								nameColumn: translate("Nome"),
								xs: 12,
								sm: 4,
								justify: "left",
								truncate: true
							},{
								idColumn: "ara001Assunto",
								nameColumn: translate("Assunto"),
								xs: 12,
								sm: 3,
								justify: "left",
								truncate: true
							},{
								idColumn: "taa008Descricao",
								nameColumn: translate("Sala"),
								xs: 12,
								sm: 3,
								justify: "left",
								truncate: true
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 12,
								sm: 4,
								justify: "left",
								truncate: true,
								cellRenderer: (row:any) => <span>{formatDate(row.ara001Dthrinicio,currentLanguage,true)} - {formatDate(row.ara001Dthrfim,currentLanguage,true,"dd/MM/yyyy")}</span>
							}]}
						/>
					</>
					}
				</RCCGrid>
			</Modal>
		</Modal>
	);

};

export default forwardRef(ModalFormMeeting);
